'use client'

import { Grid, GridType } from '@components/grid'
import { CommonFilterPanel } from '@components/grid/filter-panel'
import { Button } from '@components/button'
import { AsteriskIcon, BodyText } from 'design-configuration/components'
import { SoloViewerTokenFragment, ShowcaseFragmentFragment } from '@/gql/generated/graphql'
import { ShowcaseDisplayGridItem } from './showcase-display-grid-item'
import { AllPurposeLink } from '@components/all-purpose-link'
import { buttonVariants } from '@components/button'
import { cn } from '@/lib/utils'

type ShowcaseDisplayViewProps = {
  showcase?: ShowcaseFragmentFragment
  showcaseTokens: SoloViewerTokenFragment[]
  isProfileOwner: boolean
  showcaseFetching: boolean
  ownsAnyTokens: boolean
  onEditShowcase: () => void
  onCreateShowcase: () => void
}

export const ShowcaseDisplayView = ({
  showcase,
  showcaseTokens,
  isProfileOwner,
  showcaseFetching,
  ownsAnyTokens,
  onEditShowcase,
  onCreateShowcase
}: ShowcaseDisplayViewProps): React.ReactElement => {
  const itemCount = showcaseTokens.length

  const handleClearFilters = () => {
    // No filters for MVP
  }

  const handleOnPageNumberChange = () => {
    // No pagination for MVP
  }

  const customEmptyGrid = ownsAnyTokens ? (
    // if user has tokens but none in showcase, fallback to "create showcase" button
    <div className="py-2xl px-sm h-[406px] w-full">
      <div className="flex h-full w-full flex-col items-center justify-center">
        <div className="p-sm gap-md border-outline-variant rounded-xs flex flex-col border border-dotted text-center">
          <BodyText>Showcase your collection highlights</BodyText>
          <Button
            colorTheme="outline"
            size="small"
            onClick={showcase ? onEditShowcase : onCreateShowcase}
            iconLeft={<AsteriskIcon className="h-sm w-sm mr-xs fill-on-surface" />}
          >
            Create showcase
          </Button>
        </div>
      </div>
    </div>
  ) : (
    // if user has no tokens, fallback to "start collecting" button
    <div className="py-2xl px-sm h-[406px] w-full">
      <div className="flex h-full w-full flex-col items-center justify-center">
        <div className="p-sm gap-md border-outline-variant rounded-xs flex flex-col border border-dotted text-center">
          <BodyText>Start collecting to create a showcase</BodyText>
          <AllPurposeLink href="/discover" className={cn(buttonVariants({ colorTheme: 'outline', size: 'small' }))}>
            <AsteriskIcon className="h-sm w-sm mr-xs fill-on-surface" />
            Browse collections
          </AllPurposeLink>
        </div>
      </div>
    </div>
  )

  return (
    <div id="user-showcase" className="w-full max-w-full overflow-hidden">
      <div className="[&>*]:min-w-full [&>*]:max-w-full">
        {/* Show customize showcase button if user is viewing their own profile and has a showcase with tokens */}
        {isProfileOwner && showcase && showcaseTokens.length > 0 ? (
          <div className="p-sm flex justify-end">
            <Button
              colorTheme="outline"
              size="small"
              onClick={onEditShowcase}
              iconLeft={<AsteriskIcon className="h-sm w-sm mr-xs fill-on-surface" />}
            >
              Customize
            </Button>
          </div>
        ) : null}

        {/* Show showcase grid if user has a showcase */}
        {showcase ? (
          <Grid
            name={'user-showcase'}
            gridType={GridType.Tokens}
            // use showcase ordering always
            sortOrder="Showcase order"
            sortOptions={['Showcase order']}
            onSortOrderChange={() => undefined}
            onPageNumberChange={handleOnPageNumberChange}
            pageNumber={1}
            totalPages={1}
            totalItems={itemCount}
            numberOfActiveFilters={0}
            onClearFilters={handleClearFilters}
            gridItems={showcaseTokens}
            gridItemComponent={(item: SoloViewerTokenFragment) => (
              <ShowcaseDisplayGridItem
                token={item}
                key={`showcase-${item.id}`} // showcase items are unique, so this is safe
              />
            )}
            customEmptyGrid={customEmptyGrid}
            filterLayout={<CommonFilterPanel filters={[]} />}
            fetching={showcaseFetching}
            headerClassNameOverride="hidden"
            gridClassNameOverride={
              itemCount === 1
                ? 'gap-md relative grid h-fit w-full grid-cols-1 sm:max-w-[50%] sm:mx-auto'
                : 'gap-md relative grid h-fit grid-flow-row auto-rows-min grid-cols-1 sm:grid-cols-2'
            }
            className="px-sm pb-sm"
          />
        ) : (
          // fallback to empty grid if no showcase
          customEmptyGrid
        )}
      </div>
    </div>
  )
}
